<script setup lang="ts">
import { onMounted, ref } from "vue";
import { ElMessageBox, IElDropdownInstance } from "element-plus";
import { Close } from "@element-plus/icons-vue";
import { useRoute } from "vue-router";

import CallService from "~/projects/cc/services/CallService";
import { useDialogStore } from "~/projects/cc/stores/dialogStore";

const dialogStore = useDialogStore();
const props = defineProps({
  showEditModal: {
    type: Boolean,
  },
  phoneNo: {
    type: String,
    default: "",
  },
});

const dialerInputRef = ref<HTMLInputElement | null>(null);
watch(
  () => dialogStore.showDialer,
  async (value) => {
    if (!value) return;
    await nextTick();
    dialerInputRef.value?.focus();
  },
);

const number = ref("");

const callable = ref(true);

const addNumber = async (num: string | InputEvent) => {
  let numVal: string;
  if (Number.isNaN(num)) return;

  if (num instanceof InputEvent) number.value = num.target.value;
  else {
    numVal = num;
    if (dialogStore.inCall) {
      console.log("in call, dtmf sending is not available for agent");
      return;
    }
    number.value += numVal;
  }

  if (number.value.length > 6) {
    handleDropdownVisibility();
  }
};
const emit = defineEmits(["close"]);
const value = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

const callService = inject("callService") as CallService;
const previousNumber = ref("");
const matchedCustomer = ref("");
const clickDial = () => {
  const validLengths = [6, 7, 9, 10, 11];
  if (
    number.value !== "" &&
    !validLengths.includes(number.value.length) &&
    matchedCustomer.value === ""
  ) {
    ElMessageBox({
      type: "error",
      title: "Invalid Number",
      message: "Please enter valid phone number and try again",
    });
    return;
  }
  if (number.value === "") {
    number.value = previousNumber.value;
    return;
  }
  dialogStore.$reset();
  previousNumber.value = number.value;
  callService.makeCall(number.value);
  emit("close");

  // Send a message back to the parent (Third-Party CRM)
  if (window.opener) {
    window.opener.postMessage(
      { event: "dialed", phoneNumber: number.value },
      "http://localhost:4000",
    );
  }
};

const hitEnter = (e) => {
  e.preventDefault();
  e.stopPropagation();
  clickDial();
};

const { $listen, $unListen } = useNuxtApp();
onMounted(() => {
  const route = useRoute();
  const phoneFromUrl = route.query.phone as string;

  if (phoneFromUrl) {
    number.value = phoneFromUrl;
    dialogStore.showDialer = true;
  }

  $listen("confirmed", confirmedCallback);
  $listen("completed", completedCallback);
  $listen("incoming", openIncomingModal);

  window.addEventListener("message", (event) => {
    // if (event.origin !== "http://localhost:4000") return;

    if (event.data && event.data.phoneNumber) {
      number.value = event.data.phoneNumber;
      dialogStore.showDialer = true;
    }
  });
});

onUnmounted(() => {
  $unListen("confirmed", confirmedCallback);
  $unListen("completed", completedCallback);
  $unListen("incoming", openIncomingModal);

  // Remove event listener on unmount
  window.removeEventListener("message", () => {});
});

const confirmedCallback = () => {
  callable.value = false;
};

const completedCallback = () => {
  callable.value = true;
};

const openIncomingModal = () => {
  console.log("incoming event fired from service");
  const elem = document.getElementById("incomingUiBtn");
  if (elem) {
    elem.click();
  }
};

function close() {
  emit("close");
  console.log("i am here");
}

const dropdown = ref<IElDropdownInstance>();
const handleDropdownVisibility = function () {
  if (!dropdown.value) {
    // @ts-ignore
    dropdown.value?.handleClose();
    return;
  }
  // @ts-ignore
  dropdown.value?.handleOpen();
};

watch(
  () => dialogStore.phoneNoToSet,
  (newVal) => {
    number.value = newVal;
  },
  { immediate: true },
);
number.value = props.phoneNo || "";
</script>

<template>
  <Teleport to="body">
    <ModalMask :class="{ 'none ': !props.showEditModal }">
      <Transition name="wrapper">
        <div class="flex">
          <button
            class="dialer-button right-[381px] 2xl:right-[373px]"
            @click.prevent="close"
          >
            Dialer
          </button>
          <div
            v-if="props.showEditModal"
            ref="editDrawer"
            class="w-[400px] grid place-items-center bg-light-feature h-[100vh] overflow-clip absolute z-[500] right-0 feature-container dark:bg-dark-feature rounded-none pt-10 pr-0"
          >
            <div class="max-h-[90vh] overflow-auto w-full px-10 pt-5">
              <div
                class="dark:text-white dark:bg-dark-blue bg-white p-5 absolute w-full left-0 top-0 grid shadow-md uppercase"
              >
                <div class="flex justify-center items-center">
                  <div class="mr-auto"></div>

                  <div>Dialer</div>

                  <div class="ml-auto cursor-pointer" @click="emit('close')">
                    <el-icon><Close /></el-icon>
                  </div>
                </div>
              </div>
              <div class="border dark:border-dark-bluegray-border rounded-2xl">
                <div
                  class="bg-light-purple dark:bg-light-purple-bar rounded-2xl rounded-b-none p-7 pt-14"
                >
                  <input
                    id="dialerInputRef"
                    ref="dialerInputRef"
                    v-model="number"
                    inputmode="numeric"
                    class="bg-light-purple border-b focus:outline-none text-white dark:bg-transparent no-scrollbar dark:text-white placeholder-gray-400 dark:placeholder-gray-600"
                    placeholder="Enter a name of number"
                    type="number"
                    @keydown.enter="hitEnter"
                    @input="addNumber($event)"
                  />
                </div>
                <div
                  class="bg-white dark:bg-dark-blue flex flex-col gap-2 rounded-t-none rounded-2xl p-10 pt-2"
                >
                  <!--									<span class="text-gray-500 text-xs text-center"-->
                  <!--                  >Call from (+880) 01732986106</span-->
                  <!--                  >-->

                  <div class="grid grid-cols-3 gap-3">
                    <button
                      v-for="number in value"
                      :key="number"
                      class="shadow-[#5A55CE66]/[.5] text-[#5A55CE] p-2 2xl:p-3 text-xl 2xl:text-3xl active:ring-2 rounded-full active:ring-light-purple dark:active:ring-light-purple-bar dark:text-white bg-[#F2F5FD] dark:bg-[#303743]"
                      @click="addNumber(number)"
                    >
                      {{ number }}
                    </button>
                  </div>
                  <button
                    class="bg-green-500 rounded-full p-4 w-fit place-self-center text-white mt-1 active:ring-2 active:ring-offset-2 active:ring-green-500"
                    @click.prevent="clickDial"
                  >
                    <Svgs class="w-6" fill="currentColor" name="phone" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </ModalMask>
  </Teleport>
</template>
<style scoped>
.none {
  display: none !important;
}

.wrapper-enter-active {
  animation-name: enter;
  animation-duration: 0.5s;
}

.wrapper-leave-active {
  animation: enter 0.5s reverse;
}

@keyframes enter {
  0% {
    right: -400px;
  }

  100% {
    right: 0px;
  }
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
